import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { Html5Qrcode } from 'html5-qrcode';
import API_BASE_URL from '../apiConfig';
import './Withdraw.css';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const Withdraw = ({ wallets, setWallets }) => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation('depositWithdrawAll');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState(user?.savedAddress || '');
  const [message, setMessage] = useState('');
  const [step, setStep] = useState(address ? 2 : 1);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [attachedAddress, setAttachedAddress] = useState(address);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const html5QrCodeRef = useRef(null);
  const qrCodeRegionId = "withdrawPage-qr-scanner-frame";

  const networkFee = 1.5;
  const restrictedAmount = 0;
  const withdrawalAmount = Math.max(0, amount - networkFee);
  const [maxWithdrawalAmount, setMaxWithdrawalAmount] = useState(0);

  useEffect(() => {
    if (user && user.savedAddress) {
      setAddress(user.savedAddress);
      setStep(2);
    }
  }, [user]);

  useEffect(() => {
    if (user?.wallets?.usdt) {
      setMaxWithdrawalAmount(Math.max(0, user.wallets.usdt - restrictedAmount));
    }
  }, [user?.wallets?.usdt]);

  const validateAddress = () => {
    return address.startsWith('T') && address.length === 34;
  };

  const handleMaxAmount = () => {
    const roundedMaxWithdrawalAmount = Math.floor(maxWithdrawalAmount * 100) / 100;
    if (roundedMaxWithdrawalAmount > 0) {
      setAmount(roundedMaxWithdrawalAmount.toFixed(2));
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/api/wallet/remove-address`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setAttachedAddress('');
        setAddress('');
        setStep(1);
        setShowAddressPopup(false);
        setMessage(t('withdrawUSDT.successRemove'));
      } else {
        setMessage(t('withdrawUSDT.errorRemove'));
      }
    } catch (err) {
      setMessage(t('withdrawUSDT.errorRemove'));
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setAddress(text);
    } catch (err) {
      console.error('Clipboard read operation is not allowed', err);
    }
  };

  const escapeHTML = (input) => {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return input.replace(/[&<>"']/g, (char) => map[char]);
  };

  const handleContinue = async () => {
    if (!validateAddress()) {
      setMessage(t('withdrawUSDT.errorAddress'));
    } else {
      setMessage('');
      const sanitizedAddress = escapeHTML(address);
      try {
        const response = await apiRequest(`${API_BASE_URL}/api/wallet/save-address`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ savedAddress: sanitizedAddress })
        });
        if (response.ok) {
          setAttachedAddress(address);
          user.savedAddress = address;
          setMessage(t('withdrawUSDT.successAddress'));
          setStep(2);
        } else {
          setMessage(t('withdrawUSDT.errorAddress'));
        }
      } catch (err) {
        setMessage(t('withdrawUSDT.errorAddress'));
      }
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleWithdraw = async () => {
    if (!user?._id || !amount || !address) {
      setMessage(t('withdrawUSDT.requiredFields'));
      return;
    }
    if (user.wallets.usdt < amount) {
      setMessage(t('withdrawUSDT.insufficientBalance'));
      return;
    }
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await apiRequest(`${API_BASE_URL}/api/withdraw`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, amount, currency: 'usdt' })
      });
      const data = await response.json();
      if (response.ok) {
        setWallets(prev => ({ ...prev, ['usdt']: data.newBalance }));
        setAmount('');
        setMessage(t('withdrawUSDT.successWithdraw'));
      } else {
        setMessage(`${t('withdrawUSDT.errorWithdraw')}: ${data.error}`);
      }
    } catch (err) {
      setMessage(t('withdrawUSDT.errorWithdraw'));
    } finally {
      setIsProcessing(false);
    }
  };

  const startQRScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().then(() => {
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
        startQRScanner();
      });
      return;
    }
    const config = { fps: 10, qrbox: 200 };
    html5QrCodeRef.current = new Html5Qrcode(qrCodeRegionId);
    Html5Qrcode.getCameras().then((devices) => {
      const backCam = devices.find((d) => d.label.toLowerCase().includes("back"));
      const cameraId = backCam ? backCam.id : devices[0]?.id;
      if (cameraId) {
        html5QrCodeRef.current.start(
          cameraId,
          config,
          (decodedText) => {
            setAddress(decodedText);
            stopQRScanner();
          },
          () => {}
        ).catch(err => console.error("QR start error", err));
      }
    });
  };

  const stopQRScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().then(() => {
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
        setIsQRScannerOpen(false);
      });
    } else {
      setIsQRScannerOpen(false);
    }
  };

  return (
    <div className="withdrawPage-container">
      <h1 className="withdrawPage-title">{t('withdrawUSDT.title')}</h1>
      {/* Step 1 */}
      {step === 1 && (
        <div className="withdrawPage-step-one">
          <h3 className="withdrawPage-step-title">{t('withdrawUSDT.enterAddress')}</h3>
          <p className="withdrawPage-warning-text">{t('withdrawUSDT.addressWarning')}</p>
          <div className="withdrawPage-address-input-container">
            <div className="base-item-box withdrawPage-address-input-box">
              <input type="text" placeholder={t('withdrawUSDT.addressPlaceholder')} value={address}
                onChange={(e) => setAddress(e.target.value)} className="withdrawPage-address-input" />
            </div>
            <div className="withdrawPage-action-buttons">
              <button onClick={handlePaste} className="base-item-box withdrawPage-paste-button">{t('withdrawUSDT.paste')}</button>
              <button onClick={() => { setIsQRScannerOpen(true); setTimeout(startQRScanner, 200); }}
                className="base-item-box withdrawPage-qr-scan-button">
                <img src={commonIcons.qrScanIcon} alt={t('withdrawUSDT.scanQRAlt')} className="withdrawPage-qr-scan-icon" />
              </button>
            </div>
          </div>

          {isQRScannerOpen && (
            <div className="withdrawPage-qr-scanner-overlay">
              <div className="withdrawPage-qr-scanner-popup">
                <h3 className="withdrawPage-qr-scanner-title">{t('withdrawUSDT.qrScannerTitle')}</h3>
                <p className="withdrawPage-qr-scanner-subtitle">{t('withdrawUSDT.qrScannerSubtitle')}</p>
                <div className="withdrawPage-qr-scanner-frame" id={qrCodeRegionId}></div>
                <button className="withdrawPage-close-button" onClick={stopQRScanner}>&times;</button>
              </div>
            </div>
          )}

          {message && (
            <p className={`withdrawPage-message ${message.includes(t('withdrawUSDT.success')) ? 'withdrawPage-success-message' : 'withdrawPage-error-message'}`}>{message}</p>
          )}
          <button onClick={handleContinue} className="base-item-box withdrawPage-continue-button">{t('withdrawUSDT.continue')}</button>
        </div>
      )}

      {step === 2 && (
        <div className="withdrawPage-step-two">
          <div 
            className="withdrawPage-attached-address-container" 
            onClick={() => setShowAddressPopup(true)}
          >
            <h3 className="withdrawPage-attached-address-title">{t('withdrawUSDT.withdrawalAddress')}</h3>
            <div className="withdrawPage-attached-address">
              <p className="withdrawPage-attached-address-text">{attachedAddress}</p>
              <img src={commonIcons.arrowIcon} alt="Arrow Icon" className="withdrawPage-arrow-icon" />
            </div>
          </div>

          {showAddressPopup && (
            <div className="withdrawPage-address-popup-overlay" onClick={() => setShowAddressPopup(false)}>
              <div className="withdrawPage-address-popup">
                <h4>{attachedAddress}</h4>
                <button 
                  className="base-item-box withdrawPage-disconnect-button" 
                  onClick={handleDisconnect}
                >
                  {t('withdrawUSDT.disconnect')}
                </button>
                <button 
                  className="withdrawPage-disconnect-close-popup" 
                  onClick={() => setShowAddressPopup(false)}
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          
          <h3 className="withdrawPage-amount-title">{t('withdrawUSDT.enterAmount')}</h3>
          <div className="withdrawPage-amount-input-container">
            <div className="base-item-box withdrawPage-amount-input-box">
              <input
                type="number"
                placeholder={t('withdrawUSDT.amountPlaceholder')}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="withdrawPage-amount-input"
                onWheel={(e) => e.target.blur()}
              />
              <button 
                className="withdrawPage-max-button" 
                onClick={handleMaxAmount}
              >
                {t('withdrawUSDT.max')}: {Math.floor(maxWithdrawalAmount * 100) / 100}
              </button>
            </div>
          </div>

          {amount && amount <= networkFee && (
            <p className="withdrawPage-withdrawal-warning">
              {t('withdrawUSDT.amountWarning')}: {networkFee} USDT
            </p>
          )}

          {amount > networkFee && (
            <div className="withdrawPage-fee-info">
              <p className="withdrawPage-fee-details">
                {t('withdrawUSDT.networkFee')}: {networkFee} USDT
              </p>
              <p className="withdrawPage-fee-details">
   {t('withdrawUSDT.finalAmount')}: <strong>{withdrawalAmount.toFixed(2)} USDT</strong>
</p>
            </div>
          )}

          <div className="withdrawPage-info-section">
            <p>{t('withdrawUSDT.processingTime')}</p>
          </div>

          {message && (
            <p className={`withdrawPage-message ${
              message.includes(t('withdrawUSDT.success')) ? 'withdrawPage-success-message' : 'withdrawPage-error-message'
            }`}>
              {message}
            </p>
          )}
          
          <button 
            onClick={handleWithdraw} 
            className={`base-item-box withdrawPage-withdraw-button ${
              isProcessing || amount <= networkFee ? 'withdrawPage-withdraw-disabled' : ''
            }`}
            disabled={isProcessing || amount <= networkFee}
          >
            {isProcessing ? (
              <>
                <span className="withdrawPage-spinner"></span> {t('withdrawUSDT.processing')}
              </>
            ) : t('withdrawUSDT.requestWithdrawal')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Withdraw;